import { useMemo } from "react";
import Table, { ColumnProps } from "components/basic/table";
import { useTheme } from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, IconButton, Tooltip } from "@mui/material";
import Badge from "components/basic/badge";
import Typography from "components/basic/typography";
import GoogleRating from "components/basic/googleRating";
import VerifiedBadge from "components/basic/verifiedBadge";
import { QuoteRow } from "..";
import { formatExpiry } from "../expiryHelpers";
import { quoteStatusMeta } from "../quoteStatus";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";

export interface QuoteTableProps {
  quotes: QuoteRow[];
  onDelete: (id: number) => void;
  onView: (id: number) => void;
  onMessage: (vendorId: number) => void;
  onToggleStar?: (quote: QuoteRow) => void;
  onToggleArchive?: (quote: QuoteRow) => void;
  /** Shown instead of the table when there are no quotes. */
  emptyText?: string;
}

const QuoteTable = ({
  quotes,
  onDelete,
  onView,
  onMessage,
  onToggleStar,
  onToggleArchive,
  emptyText,
}: QuoteTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation("quotes");
  const format = useFormat();

  const columns: ColumnProps<QuoteRow>[] = useMemo(
    () => [
      {
        header: t("table.vendor"),
        align: "left",
        width: "80px",
        card: "primary",
        cell: (row: QuoteRow) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(0.5),
              cursor: "pointer",
            }}
            onClick={() => onView(row.id)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(0.5),
                // Bold until opened, like an unread message.
                fontWeight: row.viewed ? undefined : "bold",
              }}
            >
              <IconButton
                size="small"
                aria-label={t(row.starred ? "actions.unstar" : "actions.star")}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleStar?.(row);
                }}
              >
                {row.starred ? (
                  <StarIcon color="warning" />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
              {row.vendor}
            </div>
            <VerifiedBadge verified={row.isVerified} />
            <GoogleRating listing={row.googleListing} variant="compact" />
          </div>
        ),
      },
      {
        header: t("table.distance"),
        align: "left",
        card: "hidden",
        cell: (row: QuoteRow) => row.distance,
      },
      {
        header: t("table.service"),
        align: "left",
        width: "80px",
        card: "hidden",
        cell: (row: QuoteRow) => row.service,
      },
      {
        header: t("table.details"),
        align: "left",
        cell: (row: QuoteRow) => row.detail,
      },
      {
        header: t("table.cost"),
        align: "left",
        width: "80px",
        card: "primary",
        cell: (row: QuoteRow) => row.cost,
      },
      {
        header: t("table.status"),
        align: "left",
        width: "120px",
        card: "inline",
        cell: (row: QuoteRow) => {
          const meta = quoteStatusMeta(row.approvalStatus, "customer");

          return <Badge label={t(meta.label)} color={meta.color} />;
        },
      },
      {
        header: t("table.validity"),
        align: "left",
        width: "120px",
        card: "inline",
        cell: (row: QuoteRow) => {
          const expiry = formatExpiry(
            t,
            format.date,
            row.expiresAt,
            row.isExpired,
          );

          return (
            <Chip
              size="small"
              label={expiry.isExpired ? t("expiry.expired") : expiry.label}
              color={expiry.isExpired ? "default" : "success"}
              variant={expiry.isExpired ? "filled" : "outlined"}
              title={expiry.date ? `${expiry.label} (${expiry.date})` : ""}
            />
          );
        },
      },
      {
        header: t("table.date_updated"),
        align: "left",
        width: "80px",
        cell: (row: QuoteRow) => format.date(Number(row.dateUpdated)),
      },
      {
        header: "",
        align: "right",
        width: "150px",
        card: "actions",
        cell: (row: QuoteRow) => (
          <div style={{ display: "flex", gap: theme.spacing(1) }}>
            <VisibilityIcon
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={() => onView(row.id)}
            />

            <MessageIcon
              color="warning"
              style={{ cursor: "pointer" }}
              onClick={() => onMessage(row.vendorId)}
            />

            {onToggleArchive && (
              <Tooltip
                title={t(
                  row.archived ? "actions.unarchive" : "actions.archive",
                )}
              >
                {row.archived ? (
                  <UnarchiveOutlinedIcon
                    color="action"
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggleArchive(row)}
                  />
                ) : (
                  <ArchiveOutlinedIcon
                    color="action"
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggleArchive(row)}
                  />
                )}
              </Tooltip>
            )}

            <DeleteIcon
              color="error"
              style={{ cursor: "pointer" }}
              onClick={() => onDelete(row.id)}
            />
          </div>
        ),
      },
    ],
    [
      theme,
      t,
      format,
      onDelete,
      onView,
      onMessage,
      onToggleStar,
      onToggleArchive,
    ],
  );

  if (!quotes.length) {
    return (
      <Typography
        variant="body1"
        align="center"
        style={{ padding: theme.spacing(4), opacity: 0.7 }}
      >
        {emptyText ?? t("empty.all")}
      </Typography>
    );
  }

  return <Table columns={columns} data={quotes} />;
};

export default QuoteTable;
